import React, { useState, useEffect } from 'react';
import TierPageBanner from '../components/portfolio/TierPageBanner';
import TierChip from '../components/portfolio/TierChip';
import { useTierPortfolio } from '../hooks/useTierPortfolio';
import {
  RefreshCw,
  Filter,
  DollarSign,
  Activity,
  Clock,
  Database,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { apiService } from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';

interface AICallLog {
  _id: string;
  serviceName: string;
  methodName: string;
  analysisType?: string;
  correlationId?: string;
  requestIp?: string;
  environment: string;
  startTime: string;
  endTime: string;
  durationMs: number;
  provider: string;
  model: string;
  inputTokens?: number;
  outputTokens?: number;
  sourcesUsed?: number;
  costUsd?: number;
  requestType: 'FLOW' | 'ONE_OFF';
}

interface AICallStats {
  summary: {
    totalCalls: number;
    totalCost: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    totalSourcesUsed: number;
    avgDuration: number;
  };
  grouped: Array<{
    _id: string;
    count: number;
    totalCost: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    totalSourcesUsed: number;
    avgDuration: number;
  }>;
  byProvider: Array<{
    _id: string;
    count: number;
    totalCost: number;
  }>;
  byModel: Array<{
    _id: string;
    count: number;
    totalCost: number;
    provider: string;
  }>;
  byRequestType: Array<{
    _id: string;
    count: number;
    totalCost: number;
  }>;
}

const AILogs: React.FC = () => {
  const { tierFilter } = useTierPortfolio();
  const [logs, setLogs] = useState<AICallLog[]>([]);
  const [stats, setStats] = useState<AICallStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const [expandedCorrelations, setExpandedCorrelations] = useState<Set<string>>(
    new Set(),
  );
  const [correlationLogs, setCorrelationLogs] = useState<
    Record<string, AICallLog[]>
  >({});

  // Filters
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    provider: '',
    model: '',
    requestType: '',
    analysisType: '',
    correlationId: '',
    requestIp: '',
    environment: '',
    startDate: '',
    endDate: '',
    sortBy: 'startTime',
    sortOrder: 'desc' as 'asc' | 'desc',
  });

  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartGroupBy, setChartGroupBy] = useState<
    'month' | 'week' | 'day' | 'minute'
  >('day');

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Filter out empty strings and undefined values
      const cleanFilters: any = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          cleanFilters[key] = value;
        }
      });

      console.log('Loading AI logs with filters:', cleanFilters);

      const [logsData, statsData] = await Promise.all([
        apiService.getAICallLogs(cleanFilters),
        apiService.getAICallStats({
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          provider: filters.provider || undefined,
          model: filters.model || undefined,
          requestType: filters.requestType || undefined,
          groupBy: chartGroupBy, // Group by selected time period for the chart
        }),
      ]);

      console.log('AI logs response:', logsData);
      console.log('AI stats response:', statsData);

      setLogs(logsData.logs || []);
      setStats(statsData);
    } catch (error: any) {
      console.error('Error loading AI logs:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setError(
        error.response?.data?.message ||
          error.message ||
          'Failed to load AI logs. Please check the console for details.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.page,
    filters.limit,
    filters.sortBy,
    filters.sortOrder,
    chartGroupBy,
  ]);

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const applyFilters = () => {
    loadData();
  };

  const toggleLogExpansion = (logId: string) => {
    setExpandedLogs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const toggleCorrelationExpansion = async (correlationId: string) => {
    setExpandedCorrelations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(correlationId)) {
        newSet.delete(correlationId);
      } else {
        newSet.add(correlationId);
        // Load correlation logs if not already loaded
        if (!correlationLogs[correlationId]) {
          apiService
            .getAICallLogsByCorrelationId(correlationId)
            .then((logs) => {
              setCorrelationLogs((prev) => ({
                ...prev,
                [correlationId]: logs,
              }));
            });
        }
      }
      return newSet;
    });
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return 'N/A';
    return `$${value.toFixed(4)}`;
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <TierPageBanner
        title="AI logs · tier context"
        description="Prompts for thesis synthesis and tier-scoped orchestration appear here. Use the tier rail to align review with active slots."
      />
      {tierFilter !== 'all' ? (
        <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
          Focus tier: <TierChip riskTier={tierFilter} />
        </div>
      ) : null}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">AI Call Logs</h1>
        <p className="text-gray-600">
          Monitor AI API usage, costs, and performance metrics
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Summary Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Calls</p>
                  <p className="text-2xl font-bold">
                    {stats.summary.totalCalls}
                  </p>
                </div>
                <Activity className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Cost</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(stats.summary.totalCost)}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Tokens</p>
                  <p className="text-2xl font-bold">
                    {(
                      (stats.summary.totalInputTokens || 0) +
                      (stats.summary.totalOutputTokens || 0)
                    ).toLocaleString()}
                  </p>
                </div>
                <Database className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Avg Duration</p>
                  <p className="text-2xl font-bold">
                    {formatDuration(stats.summary.avgDuration || 0)}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Cost Over Time Chart */}
      {stats && stats.grouped && stats.grouped.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Cost Over Time</CardTitle>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Time Frame:</label>
                <select
                  value={chartGroupBy}
                  onChange={(e) =>
                    setChartGroupBy(
                      e.target.value as 'month' | 'week' | 'day' | 'minute',
                    )
                  }
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value="month">Month</option>
                  <option value="week">Week</option>
                  <option value="day">Day</option>
                  <option value="minute">Minute</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={stats.grouped
                  .slice()
                  .reverse()
                  .map((item) => ({
                    time: item._id,
                    cost: parseFloat((item.totalCost || 0).toFixed(4)),
                  }))}
                margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis
                  dataKey="time"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                  style={{ fontSize: '11px' }}
                  tick={{ fill: '#6b7280' }}
                />
                <YAxis
                  tickFormatter={(value) => `$${value.toFixed(2)}`}
                  style={{ fontSize: '12px' }}
                  tick={{ fill: '#6b7280' }}
                  label={{
                    value: 'Cost (USD)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { textAnchor: 'middle', fill: '#6b7280' },
                  }}
                />
                <Tooltip
                  formatter={(value: number) => [
                    `$${value.toFixed(4)}`,
                    'Cost',
                  ]}
                  labelStyle={{ color: '#000', fontWeight: 'bold' }}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                  }}
                />
                <Bar
                  dataKey="cost"
                  fill="#3b82f6"
                  name="Cost (USD)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Breakdown Stats */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader>
              <CardTitle>By Provider</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats.byProvider.map((item) => (
                  <div
                    key={item._id}
                    className="flex justify-between items-center"
                  >
                    <span className="font-medium">{item._id}</span>
                    <div className="text-right">
                      <div className="text-sm font-bold">
                        {formatCurrency(item.totalCost)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {item.count} calls
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>By Model</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {stats.byModel.map((item) => (
                  <div
                    key={item._id}
                    className="flex justify-between items-center"
                  >
                    <div>
                      <span className="font-medium">{item._id}</span>
                      <div className="text-xs text-gray-500">
                        {item.provider}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold">
                        {formatCurrency(item.totalCost)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {item.count} calls
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>By Request Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats.byRequestType.map((item) => (
                  <div
                    key={item._id}
                    className="flex justify-between items-center"
                  >
                    <span className="font-medium">{item._id}</span>
                    <div className="text-right">
                      <div className="text-sm font-bold">
                        {formatCurrency(item.totalCost)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {item.count} calls
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Filters</CardTitle>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-sm"
            >
              <Filter className="h-4 w-4" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
          </div>
        </CardHeader>
        {showFilters && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Provider
                </label>
                <select
                  value={filters.provider}
                  onChange={(e) =>
                    handleFilterChange('provider', e.target.value)
                  }
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">All</option>
                  <option value="grokai">GrokAI</option>
                  <option value="openai">OpenAI</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Model</label>
                <input
                  type="text"
                  value={filters.model}
                  onChange={(e) => handleFilterChange('model', e.target.value)}
                  placeholder="e.g., grok-4"
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Request Type
                </label>
                <select
                  value={filters.requestType}
                  onChange={(e) =>
                    handleFilterChange('requestType', e.target.value)
                  }
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">All</option>
                  <option value="FLOW">FLOW</option>
                  <option value="ONE_OFF">ONE_OFF</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Analysis Type
                </label>
                <input
                  type="text"
                  value={filters.analysisType}
                  onChange={(e) =>
                    handleFilterChange('analysisType', e.target.value)
                  }
                  placeholder="e.g., comprehensive"
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Correlation ID
                </label>
                <input
                  type="text"
                  value={filters.correlationId}
                  onChange={(e) =>
                    handleFilterChange('correlationId', e.target.value)
                  }
                  placeholder="Filter by correlation"
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange('startDate', e.target.value)
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  End Date
                </label>
                <input
                  type="datetime-local"
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange('endDate', e.target.value)
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Environment
                </label>
                <select
                  value={filters.environment}
                  onChange={(e) =>
                    handleFilterChange('environment', e.target.value)
                  }
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">All</option>
                  <option value="production">Production</option>
                  <option value="development">Development</option>
                </select>
              </div>
            </div>

            <div className="mt-4 flex gap-2">
              <button
                onClick={applyFilters}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Apply Filters
              </button>
              <button
                onClick={() => {
                  setFilters({
                    page: 1,
                    limit: 50,
                    provider: '',
                    model: '',
                    requestType: '',
                    analysisType: '',
                    correlationId: '',
                    requestIp: '',
                    environment: '',
                    startDate: '',
                    endDate: '',
                    sortBy: 'startTime',
                    sortOrder: 'desc',
                  });
                  setTimeout(loadData, 100);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Clear Filters
              </button>
              <button
                onClick={loadData}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>AI Call Logs</CardTitle>
            {loading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Loading...
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading && logs.length === 0 ? (
            <div className="text-center py-8">
              <LoadingSpinner />
              <p className="mt-4 text-gray-500">Loading AI call logs...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Time</th>
                    <th className="text-left p-2">Provider</th>
                    <th className="text-left p-2">Model</th>
                    <th className="text-left p-2">Type</th>
                    <th className="text-right p-2">Cost</th>
                    <th className="text-right p-2">Tokens</th>
                    <th className="text-right p-2">Duration</th>
                    <th className="text-left p-2">Correlation</th>
                    <th className="text-left p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <React.Fragment key={log._id}>
                      <tr
                        className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => toggleLogExpansion(log._id)}
                      >
                        <td className="p-2">{formatDate(log.startTime)}</td>
                        <td className="p-2">{log.provider}</td>
                        <td className="p-2">{log.model}</td>
                        <td className="p-2">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                            {log.requestType}
                          </span>
                          {log.analysisType && (
                            <div className="text-xs text-gray-500 mt-1">
                              {log.analysisType}
                            </div>
                          )}
                        </td>
                        <td className="p-2 text-right font-medium">
                          {formatCurrency(log.costUsd)}
                        </td>
                        <td className="p-2 text-right">
                          {(
                            (log.inputTokens || 0) + (log.outputTokens || 0)
                          ).toLocaleString()}
                        </td>
                        <td className="p-2 text-right">
                          {formatDuration(log.durationMs)}
                        </td>
                        <td className="p-2">
                          {log.correlationId ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCorrelationExpansion(log.correlationId!);
                              }}
                              className="text-blue-500 hover:underline text-xs flex items-center gap-1"
                            >
                              {expandedCorrelations.has(log.correlationId!) ? (
                                <ChevronDown className="h-3 w-3" />
                              ) : (
                                <ChevronRight className="h-3 w-3" />
                              )}
                              {log.correlationId.substring(0, 8)}...
                            </button>
                          ) : (
                            <span className="text-gray-400 text-xs">N/A</span>
                          )}
                        </td>
                        <td className="p-2">
                          {expandedLogs.has(log._id) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </td>
                      </tr>
                      {expandedLogs.has(log._id) && (
                        <tr>
                          <td colSpan={9} className="p-4 bg-gray-50">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <div className="font-medium">Service</div>
                                <div className="text-gray-600">
                                  {log.serviceName}.{log.methodName}
                                </div>
                              </div>
                              <div>
                                <div className="font-medium">Input Tokens</div>
                                <div className="text-gray-600">
                                  {(log.inputTokens || 0).toLocaleString()}
                                </div>
                              </div>
                              <div>
                                <div className="font-medium">Output Tokens</div>
                                <div className="text-gray-600">
                                  {(log.outputTokens || 0).toLocaleString()}
                                </div>
                              </div>
                              <div>
                                <div className="font-medium">Sources Used</div>
                                <div className="text-gray-600">
                                  {log.sourcesUsed || 0}
                                </div>
                              </div>
                              <div>
                                <div className="font-medium">Request IP</div>
                                <div className="text-gray-600">
                                  {log.requestIp || 'N/A'}
                                </div>
                              </div>
                              <div>
                                <div className="font-medium">Environment</div>
                                <div className="text-gray-600">
                                  {log.environment}
                                </div>
                              </div>
                              <div>
                                <div className="font-medium">Start Time</div>
                                <div className="text-gray-600">
                                  {formatDate(log.startTime)}
                                </div>
                              </div>
                              <div>
                                <div className="font-medium">End Time</div>
                                <div className="text-gray-600">
                                  {formatDate(log.endTime)}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {log.correlationId &&
                        expandedCorrelations.has(log.correlationId) &&
                        correlationLogs[log.correlationId] && (
                          <tr>
                            <td colSpan={9} className="p-4 bg-blue-50">
                              <div className="mb-2 font-medium">
                                Related Calls (
                                {correlationLogs[log.correlationId].length})
                              </div>
                              <div className="space-y-2">
                                {correlationLogs[log.correlationId].map(
                                  (relatedLog) => (
                                    <div
                                      key={relatedLog._id}
                                      className="text-sm border-l-2 border-blue-300 pl-2"
                                    >
                                      <div className="flex justify-between">
                                        <span>
                                          {relatedLog.model} -{' '}
                                          {relatedLog.analysisType || 'N/A'}
                                        </span>
                                        <span className="font-medium">
                                          {formatCurrency(relatedLog.costUsd)}
                                        </span>
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {formatDate(relatedLog.startTime)}
                                      </div>
                                    </div>
                                  ),
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              {!loading && logs.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p className="mb-2">No logs found matching your filters</p>
                  <p className="text-sm">
                    Try adjusting your filters or check if there are any AI
                    calls in the database.
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AILogs;
